import React from "react";
import { Link } from "react-router-dom";
import { Leaf, Instagram } from "lucide-react";

const cols = [
  {
    heading: "Explore",
    links: [
      { to: "/about", label: "About" },
      { to: "/resources", label: "Resources" },
      { to: "/the-strong-force-daily", label: "The Strong Force Daily" },
      { to: "/inner-circle", label: "Community" },
      { to: "/contact", label: "Contact" },
    ],
  },
  {
    heading: "Support",
    links: [
      { to: "/shop", label: "Shop" },
      { to: "/inner-circle", label: "Inner Circle" },
      { to: "/contact", label: "Help" },
      { to: "/privacy", label: "Privacy Policy" },
      { to: "/terms", label: "Terms" },
    ],
  },
];

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M19.5 6.6a5.6 5.6 0 0 1-3.4-1.2 5.7 5.7 0 0 1-2-3H10v12.1a2.9 2.9 0 1 1-2-2.7V8.7a5.9 5.9 0 1 0 5 5.8V10a8.8 8.8 0 0 0 4.6 1.3z"/>
  </svg>
);
const PinterestIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M12 2a10 10 0 0 0-3.6 19.3c-.1-.8-.2-2 0-2.9l1.2-5s-.3-.6-.3-1.5c0-1.4.8-2.4 1.8-2.4.9 0 1.3.7 1.3 1.5 0 .9-.6 2.2-.9 3.5-.2 1 .5 1.9 1.5 1.9 1.9 0 3.3-2 3.3-4.8 0-2.5-1.8-4.3-4.4-4.3-3 0-4.8 2.3-4.8 4.6 0 .9.3 1.9.8 2.4.1.1.1.2.1.3l-.3 1c0 .2-.2.3-.4.2-1.4-.6-2.2-2.6-2.2-4.2 0-3.4 2.5-6.6 7.2-6.6 3.8 0 6.7 2.7 6.7 6.3 0 3.8-2.4 6.8-5.7 6.8-1.1 0-2.2-.6-2.5-1.3l-.7 2.6c-.2.9-.9 2.1-1.4 2.8A10 10 0 1 0 12 2z"/>
  </svg>
);

const socials = [
  { href: "https://instagram.com/TheStrongForce.Daily", label: "Instagram", Icon: Instagram, sub: "@TheStrongForce.Daily" },
  { href: "https://www.tiktok.com/@thestrongforcedaily", label: "TikTok", Icon: TikTokIcon, sub: "@thestrongforcedaily" },
  { href: "https://www.tiktok.com/@wish.ingwellness", label: "TikTok Wellness", Icon: TikTokIcon, sub: "@wish.ingwellness" },
  { href: "https://pin.it/1Xjp7YwfZ", label: "Pinterest", Icon: PinterestIcon, sub: "Pinterest" },
  { href: "mailto:support@wishingwellnesslife.com", label: "Email", Icon: () => (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>
      </svg>
    ), sub: "support@wishingwellnesslife.com" },
];

export default function BrandFooter() {
  return (
    <footer className="bg-[#F8F6F2] pt-20 pb-10 mt-24 border-t border-[#EDE9E1]" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#8FAF8A]">
              <Leaf className="h-5 w-5 text-white" />
            </span>
            <span className="font-serif-display font-medium text-xl text-[#333333]">Wishing Wellness</span>
          </div>
          <p className="mt-5 text-[#4A4A4A] text-base leading-relaxed max-w-sm">
            A trusted brand for whole-person wellness — practical resources, journals, and daily encouragement to help you become your healthiest self.
          </p>
          <div className="mt-6 flex items-center gap-3">
            {socials.map(({ href, label, Icon, sub }) => (
              <a
                key={label}
                href={href}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                aria-label={`${label} — ${sub}`}
                title={sub}
                data-testid={`social-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white border border-[#EDE9E1] text-[#4A4A4A] hover:text-[#8FAF8A] hover:border-[#8FAF8A] transition-colors"
              >
                <Icon className="h-4.5 w-4.5" />
              </a>
            ))}
          </div>
          <a
            href="mailto:support@wishingwellnesslife.com"
            className="mt-4 inline-block text-sm text-[#4A4A4A] hover:text-[#333333] transition-colors"
            data-testid="footer-support-email"
          >
            support@wishingwellnesslife.com
          </a>
        </div>

        {cols.map((c) => (
          <div key={c.heading} className="md:col-span-3">
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#8FAF8A] mb-4">{c.heading}</div>
            <ul className="space-y-3">
              {c.links.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="text-[#4A4A4A] hover:text-[#333333] transition-colors"
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-10 mt-14 pt-6 border-t border-[#EDE9E1] flex flex-col md:flex-row md:justify-between gap-2 text-xs text-[#8A8A8A]">
        <span>© {new Date().getFullYear()} Wishing Wellness. All rights reserved.</span>
        <span>Progress, every single day.</span>
      </div>
    </footer>
  );
}
